import { CartLinesUpdateEvent } from '@shopify/events';

const root = window.Shopify?.routes?.root || '/';
const dialog = /** @type {HTMLDialogElement | null} */ (document.querySelector('[data-lc-qa]'));
const toast = document.querySelector('[data-lc-toast]');
const labels = dialog?.dataset ?? {};
const money = new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: labels.currency || 'USD' });
const products = new Map();

const fetchProduct = (handle) => {
  if (!products.has(handle)) {
    products.set(
      handle,
      fetch(`${root}products/${encodeURIComponent(handle)}.js`).then((r) => {
        if (!r.ok) throw new Error('Product not found');
        return r.json();
      })
    );
  }
  return products.get(handle);
};

let toastTimer = 0;
const showToast = (message) => {
  if (!toast) return;
  toast.textContent = message;
  toast.hidden = false;
  clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => (toast.hidden = true), 4500);
};

/** Adds through Shopify's cart API and broadcasts Horizon's cart event so the drawer, count and progress refresh. */
const addToCart = async (source, id, quantity) => {
  const sectionIds = [...document.querySelectorAll('cart-items-component')]
    .map((el) => /** @type {HTMLElement} */ (el).dataset.sectionId)
    .filter(Boolean);
  const deferred = CartLinesUpdateEvent.createPromise();
  source.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: String(id), quantity }],
      promise: deferred.promise,
    })
  );
  let data = {};
  let ok = false;
  try {
    const response = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id, quantity }], sections: sectionIds.join(',') }),
    });
    data = await response.json();
    ok = response.ok && !data.status;
  } catch {
    ok = false;
  }
  const cart = await fetch(`${root}cart.js`).then((r) => r.json());
  deferred.resolve({
    cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
    detail: {
      items: cart.items,
      source: 'lamour-quick-add',
      itemCount: quantity,
      sections: ok ? data.sections : undefined,
      didError: !ok,
    },
  });
  if (!ok) throw new Error(data.description || data.message || labels.labelError);
};

/* ---------- Quick add sheet ---------- */
const q = (sel) => dialog?.querySelector(sel);
let state = { product: null, selected: [], source: null };

const findVariant = (selected) =>
  state.product.variants.find((v) => v.options.every((value, i) => value === selected[i]));

const isPossible = (index, value) =>
  state.product.variants.some(
    (v) => v.available && v.options[index] === value && v.options.every((val, i) => i === index || val === state.selected[i])
  );

const render = () => {
  const { product, selected } = state;
  const variant = findVariant(selected);
  const qty = Math.max(1, Number.parseInt(q('[data-lc-qa-qty]').value, 10) || 1);
  const image = variant?.featured_image?.src || product.featured_image;
  const img = q('[data-lc-qa-img]');
  if (image) img.src = `${image}${image.includes('?') ? '&' : '?'}width=800`;
  img.hidden = !image;
  q('[data-lc-qa-price]').textContent = variant ? money.format(variant.price / 100) : '';
  for (const fieldset of q('[data-lc-qa-options]').querySelectorAll('fieldset')) {
    const index = Number(fieldset.dataset.index);
    for (const input of fieldset.querySelectorAll('input')) {
      input.checked = input.value === selected[index];
      input.closest('label').classList.toggle('is-unavailable', !isPossible(index, input.value));
    }
  }
  const add = q('[data-lc-qa-add]');
  const label = q('[data-lc-qa-add-label]');
  if (!variant) {
    add.disabled = true;
    label.textContent = labels.labelUnavailable;
  } else if (!variant.available) {
    add.disabled = true;
    label.textContent = labels.labelSoldOut;
  } else {
    add.disabled = false;
    label.textContent = `${labels.labelAdd} · ${money.format((variant.price * qty) / 100)}`;
  }
};

const openSheet = (product, source) => {
  const first = product.variants.find((v) => v.available) || product.variants[0];
  state = { product, selected: [...first.options], source };
  const vendor =
    product.vendor && product.vendor !== labels.shopName && !product.title.toLowerCase().startsWith(product.vendor.toLowerCase())
      ? product.vendor
      : '';
  q('[data-lc-qa-vendor]').textContent = vendor;
  q('[data-lc-qa-vendor]').hidden = !vendor;
  q('[data-lc-qa-title]').textContent = product.title;
  q('[data-lc-qa-link]').href = product.url || `${root}products/${product.handle}`;
  q('[data-lc-qa-qty]').value = '1';
  const options = q('[data-lc-qa-options]');
  options.replaceChildren(
    ...product.options.map((option, index) => {
      const fieldset = document.createElement('fieldset');
      fieldset.dataset.index = String(index);
      const legend = document.createElement('legend');
      legend.textContent = option.name;
      fieldset.append(legend);
      const group = document.createElement('div');
      group.className = 'lc-qa-pills';
      for (const value of option.values) {
        const label = document.createElement('label');
        label.className = 'lc-qa-pill';
        const input = document.createElement('input');
        input.type = 'radio';
        input.name = `lc-qa-${index}`;
        input.value = value;
        input.addEventListener('change', () => {
          state.selected[index] = value;
          const exact = findVariant(state.selected);
          if (!exact?.available) {
            const fallback = state.product.variants.find((v) => v.available && v.options[index] === value);
            if (fallback) state.selected = [...fallback.options];
          }
          render();
        });
        const span = document.createElement('span');
        span.textContent = value;
        label.append(input, span);
        group.append(label);
      }
      fieldset.append(group);
      return fieldset;
    })
  );
  render();
  dialog.showModal();
  dialog.querySelector('input:checked')?.focus();
};

if (dialog) {
  q('[data-lc-qa-close]').addEventListener('click', () => dialog.close());
  dialog.addEventListener('click', (event) => {
    if (event.target === dialog) dialog.close();
  });
  q('[data-lc-qa-qty]').addEventListener('input', render);
  for (const step of dialog.querySelectorAll('[data-lc-qa-step]')) {
    step.addEventListener('click', () => {
      const input = q('[data-lc-qa-qty]');
      input.value = String(Math.max(1, (Number.parseInt(input.value, 10) || 1) + Number(step.dataset.lcQaStep)));
      render();
    });
  }
  q('[data-lc-qa-form]').addEventListener('submit', async (event) => {
    event.preventDefault();
    const variant = findVariant(state.selected);
    if (!variant?.available) return;
    const quantity = Math.max(1, Number.parseInt(q('[data-lc-qa-qty]').value, 10) || 1);
    const source = state.source?.isConnected ? state.source : document.body;
    dialog.close();
    try {
      await addToCart(source, variant.id, quantity);
    } catch (error) {
      showToast(error.message || labels.labelError);
    }
  });
}

/* ---------- Card buttons ---------- */
document.addEventListener('click', async (event) => {
  const button = event.target instanceof Element ? event.target.closest('[data-lc-quick-add]') : null;
  if (!button || !dialog || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  event.preventDefault();
  if (button.getAttribute('aria-busy') === 'true') return;
  button.setAttribute('aria-busy', 'true');
  button.classList.add('is-loading');
  try {
    const product = await fetchProduct(button.dataset.lcQuickAdd);
    const available = product.variants.filter((v) => v.available);
    if (!available.length) {
      window.location.href = button.href;
    } else if (product.variants.length === 1) {
      await addToCart(button, available[0].id, 1);
    } else {
      openSheet(product, button);
    }
  } catch (error) {
    if (error?.message && error.message !== 'Product not found') showToast(error.message);
    else window.location.href = button.href;
  } finally {
    button.removeAttribute('aria-busy');
    button.classList.remove('is-loading');
  }
});
